import React, { useRef, useState } from 'react';
import StatusIcon from './StatusIcon';

function ProjectRow({
  activity,
  index,      // índice da linha dentro de `activities`
  onEditInline,
  onEditChange,
  onEditSave,
  isSelected,
  onSelectToggle,
  editable = false,
  onReorder   // função que recebe (stageId, fromIndex, toIndex)
}) {
  const rowRef = useRef(null);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [editingActivity, setEditingActivity] = useState(false);

  const handleChange = (field, value) => {
    if (!editable || !onEditChange) return;
    let updated = { ...activity };
    if (field === 'status' && value === 'Concluído') {
      updated.dataConclusao = new Date().toISOString().split('T')[0];
    }
    updated[field] = value;
    onEditChange(updated);
  };

  const handleCellClick = e => {
    if (!editable) return;
    const isButton = e.target.closest('button');
    const isCheckbox = e.target.closest('input[type="checkbox"]');
    if (isButton || isCheckbox) return;
    setEditingActivity(true);
    if (onEditInline) onEditInline(activity);
  };

  const handleBlur = e => {
    if (!editable) return;
    if (!rowRef.current.contains(e.relatedTarget)) {
      if (onEditSave) onEditSave(activity);
      setEditingActivity(false);
    }
  };

  const addComment = () => {
    if (!editable || !newComment.trim()) return;
    const updatedComments = activity.comments ? [...activity.comments] : [];
    updatedComments.push({ text: newComment.trim(), date: new Date().toISOString() });
    const updated = { ...activity, comments: updatedComments };
    if (onEditChange) onEditChange(updated);
    if (onEditSave) onEditSave(updated);
    setNewComment('');
  };

  const compareDates = (a, b) => {
    if (!a || !b) return 'text-gray-500';
    return a <= b ? 'text-green-600' : 'text-red-600';
  };

  return (
    <>
      <tr
        ref={rowRef}
        className={`border-b ${editable ? 'hover:bg-gray-50 cursor-pointer' : ''}`}
        onClick={handleCellClick}
        onBlur={handleBlur}
        tabIndex={-1}
        draggable={!!onReorder}
        onDragStart={e => {
          e.dataTransfer.setData('text/plain', index);
        }}
        onDragOver={e => e.preventDefault()}
        onDrop={e => {
          e.preventDefault();
          const from = parseInt(e.dataTransfer.getData('text/plain'), 10);
          const to = index;
          const stageId = activity.stageId; // pegando stageId diretamente de activity
          if (from !== to && onReorder) {
            onReorder(stageId, from, to);
          }
        }}
      >
        {/* Checkbox */}
        <td className="p-2">
          {editable && (
            <input
              type="checkbox"
              checked={isSelected}
              onClick={e => e.stopPropagation()}
              onChange={() => onSelectToggle(activity)}
            />
          )}
        </td>

        {/* Botão Notas */}
        <td className="p-2">
          <button
            onClick={e => {
              e.stopPropagation();
              setCommentsOpen(open => !open);
            }}
            className="p-1 hover:bg-gray-200 rounded"
            type="button"
          >
            🗨️
          </button>
        </td>

        {/* Título da Atividade */}
        <td className="p-2">
          {editingActivity && editable ? (
            <input
              value={activity.title}
              onChange={e => handleChange('title', e.target.value)}
              className="border rounded px-1 w-full"
              autoFocus
            />
          ) : (
            activity.title
          )}
        </td>

        {/* Situação */}
        <td className="p-2">
          {editingActivity && editable ? (
            <select
              value={activity.status}
              onChange={e => handleChange('status', e.target.value)}
              className="border rounded w-full"
            >
              <option>Pendente</option>
              <option>Em andamento</option>
              <option>Concluído</option>
            </select>
          ) : (
            <>
              <StatusIcon
                type={
                  activity.status === 'Concluído'
                    ? 'success'
                    : activity.status === 'Em andamento'
                    ? 'warning'
                    : 'error'
                }
              />
              <span className="ml-2">{activity.status}</span>
            </>
          )}
        </td>

        {/* Responsável */}
        <td className="p-2">
          {editingActivity && editable ? (
            <select
              value={activity.responsible || ''}
              onChange={e => handleChange('responsible', e.target.value)}
              className="border rounded w-full"
            >
              <option value="">Selecione</option>
              <option value="Escritório">Escritório</option>
              <option value="Cliente">Cliente</option>
            </select>
          ) : (
            activity.responsible
          )}
        </td>

        {/* Término Previsto */}
        <td className="p-2">
          {editingActivity && editable ? (
            <input
              type="date"
              value={activity.plannedEnd}
              onChange={e => handleChange('plannedEnd', e.target.value)}
              className="border rounded px-1 w-full"
            />
          ) : (
            activity.plannedEnd
          )}
        </td>

        {/* Término Real */}
        <td className={`p-2 ${compareDates(activity.dataConclusao, activity.plannedEnd)}`}>
          {activity.dataConclusao || '-'}
        </td>
      </tr>

      {commentsOpen && (
        <tr>
          <td colSpan={7} className="bg-gray-50 p-4">
            <div className="space-y-2">
              {activity.comments && activity.comments.length ? (
                activity.comments.map((c, i) => (
                  <div key={i} className="border rounded p-2 bg-white shadow-sm">
                    <p className="text-gray-700">{c.text}</p>
                    <small className="text-gray-500">
                      {new Date(c.date).toLocaleString()}
                    </small>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 italic">Sem comentários ainda.</p>
              )}
              {editable && (
                <div className="flex space-x-2 mt-2">
                  <input
                    type="text"
                    placeholder="Novo comentário"
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addComment()}
                    className="flex-grow border rounded px-3 py-2"
                  />
                  <button
                    onClick={addComment}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                    type="button"
                  >
                    Salvar
                  </button>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default ProjectRow;
